import React, { useEffect, useMemo, useState } from "react";

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

function onlyNumberOrEmpty(v) {
  const s = (v ?? "").toString().trim();
  if (!s) return "";
  const norm = s.replace(",", ".");
  if (!/^\d+(\.\d+)?$/.test(norm)) return s;
  return norm;
}

export default function CriarObjetivosDialogIndividuais({
  open,
  onClose,
  onApply,

  // array atual [{ refvendedor, qtd, euros, litros, clientes }]
  value = [],

  // opções [{value,label}]
  vendedoresOptions = [],
  refsEquipa = [],

  loading = false,

  usaQtd = true,
  usaEuros = true,
  usaLitros = true,
  usaClientes = true,
}) {
  const [vendedoresSel, setVendedoresSel] = useState([]); // array {value,label}
  const [linhas, setLinhas] = useState({}); // por refvendedor: {qtd, euros, litros, clientes}

  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    if (!open) return;

    setHydrated(false);

    setVendedoresSel(vendedoresDisponiveis || []);

    const obj = {};
    for (const it of value || []) {
      const r = String(it.refvendedor);
      obj[r] = {
        qtd: it.qtd != null ? String(it.qtd) : "",
        euros: it.euros != null ? String(it.euros) : "",
        litros: it.litros != null ? String(it.litros) : "",
        clientes: it.clientes != null ? String(it.clientes) : "",
      };
    }
    setLinhas(obj);

    // ✅ garantir 1 frame com hydrated=false (spinner visível)
    requestAnimationFrame(() => setHydrated(true));
  }, [open, value, vendedoresOptions]);

  const refsEquipaSet = useMemo(
    () => new Set((refsEquipa || []).map((r) => String(r))),
    [refsEquipa]
  );

  const vendedoresDisponiveis = useMemo(
    () => (vendedoresOptions || []).filter((v) => !refsEquipaSet.has(String(v.value))),
    [vendedoresOptions, refsEquipaSet]
  );

  const refsSelecionados = useMemo(
    () => vendedoresDisponiveis.map((x) => String(x.value)).filter(Boolean),
    [vendedoresDisponiveis]
  );

  // garantir que existem linhas para refs selecionadas
  useEffect(() => {
    if (!open || !hydrated) return;

    setLinhas((prev) => {
      const novo = { ...(prev || {}) };
      for (const r of refsSelecionados) {
        if (!novo[r]) {
          novo[r] = { qtd: "", euros: "", litros: "", clientes: "" };
        }
      }
      // remover linhas de vendedores deselecionados
      Object.keys(novo).forEach((k) => {
        if (!refsSelecionados.includes(k)) delete novo[k];
      });
      return novo;
    });
  }, [refsSelecionados, open]);

  const setCampo = (ref, campo, valor) => {
    setLinhas((prev) => ({
      ...(prev || {}),
      [ref]: { ...(prev?.[ref] || {}), [campo]: valor },
    }));
  };

  const handleApply = () => {
    // construir payload
    const out = refsSelecionados
      .map((r) => {
        const l = linhas?.[r] || {};

        const qtdVal = usaQtd && (l.qtd ?? "").toString().trim() !== "" ? Number(l.qtd) : null;
        const eurosVal =
          usaEuros && (l.euros ?? "").toString().trim() !== "" ? Number(l.euros) : null;
        const litrosVal =
          usaLitros && (l.litros ?? "").toString().trim() !== "" ? Number(l.litros) : null;
        const clientesVal =
          usaClientes && (l.clientes ?? "").toString().trim() !== "" ? Number(l.clientes) : null;

        return {
          refvendedor: r,
          qtd: qtdVal,
          euros: eurosVal,
          litros: litrosVal,
          clientes: clientesVal,
        };
      })
      .filter(
        (x) =>
          Number(x.qtd || 0) > 0 ||
          Number(x.euros || 0) > 0 ||
          Number(x.litros || 0) > 0 ||
          Number(x.clientes || 0) > 0
      );

    onApply(out);
  };

  const podeAplicar = refsSelecionados.length > 0;

  const handleEnterFixar = (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    e.stopPropagation();

    // força commit do valor (como clicar fora)
    e.currentTarget.blur();
  };

  const totais = useMemo(() => {
    const acc = { qtd: 0, euros: 0, litros: 0, clientes: 0 };

    const toNum = (v) => {
      const s = (v ?? "").toString().trim();
      if (!s) return 0;
      const n = Number(s.replace(",", "."));
      return Number.isFinite(n) ? n : 0;
    };

    for (const r of refsSelecionados) {
      const l = linhas?.[r] || {};
      if (usaQtd) acc.qtd += toNum(l.qtd);
      if (usaEuros) acc.euros += toNum(l.euros);
      if (usaLitros) acc.litros += toNum(l.litros);
      if (usaClientes) acc.clientes += toNum(l.clientes);
    }

    return acc;
  }, [refsSelecionados, linhas, usaQtd, usaEuros, usaLitros, usaClientes]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Objetivos Individuais</DialogTitle>

      <MDBox
        px={3}
        py={2}
        sx={{
          borderBottom: "1px solid rgba(0,0,0,0.12)",
          backgroundColor: "rgba(0,0,0,0.02)",
        }}
      >
        <MDBox display="flex" flexWrap="wrap" gap={2} alignItems="center">
          {usaQtd && (
            <MDBox sx={{ minWidth: 90 }}>
              <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5 }}>
                Quantidade
              </MDTypography>
              <TextField
                value={totais.qtd ? String(totais.qtd) : ""}
                InputProps={{ readOnly: true }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13, width: 90 } }}
              />
            </MDBox>
          )}

          {usaEuros && (
            <MDBox sx={{ minWidth: 90 }}>
              <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5 }}>
                Euros
              </MDTypography>
              <TextField
                value={totais.euros ? String(totais.euros) : ""}
                InputProps={{ readOnly: true }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13, width: 90 } }}
              />
            </MDBox>
          )}

          {usaLitros && (
            <MDBox sx={{ minWidth: 90 }}>
              <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5 }}>
                Litros
              </MDTypography>
              <TextField
                value={totais.litros ? String(totais.litros) : ""}
                InputProps={{ readOnly: true }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13, width: 90 } }}
              />
            </MDBox>
          )}

          {usaClientes && (
            <MDBox sx={{ minWidth: 90 }}>
              <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5 }}>
                Clientes
              </MDTypography>
              <TextField
                value={totais.clientes ? String(totais.clientes) : ""}
                InputProps={{ readOnly: true }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13, width: 90 } }}
              />
            </MDBox>
          )}
        </MDBox>
      </MDBox>

      <DialogContent dividers>
        {loading || !hydrated || (vendedoresOptions?.length || 0) === 0 ? (
          <MDBox py={4} display="flex" alignItems="center" justifyContent="center" gap={1}>
            <CircularProgress size={22} />
            <MDTypography variant="button" sx={{ fontSize: 12, opacity: 0.8 }}>
              A carregar objetivos individuais...
            </MDTypography>
          </MDBox>
        ) : (
          <>
            <MDBox mb={2}>
              <MDTypography variant="caption" color="text">
                Seleciona um ou mais vendedores e define os mínimos individuais.
              </MDTypography>
            </MDBox>

            {refsSelecionados.length > 0 && (
              <MDBox mt={2}>
                <MDTypography variant="button" sx={{ fontSize: 12, mb: 1, display: "block" }}>
                  Metas por vendedor
                </MDTypography>

                {refsSelecionados.map((r) => {
                  const vendedorLabel =
                    (vendedoresOptions || []).find((x) => String(x.value) === String(r))?.label ||
                    r;

                  const l = linhas?.[r] || {};

                  return (
                    <MDBox key={r} mb={0.5} py={0.5} px={0}>
                      <MDBox display="flex" alignItems="flex-end" gap={2} flexWrap="wrap">
                        <MDBox
                          sx={{
                            width: 180,
                            minWidth: 180,
                            pb: 1.2,
                            pr: 2,
                            textAlign: "right",
                            fontWeight: 600,
                            fontSize: 13,
                          }}
                        >
                          {vendedorLabel}
                        </MDBox>
                        {usaQtd && (
                          <MDBox sx={{ minWidth: 90 }}>
                            <MDTypography
                              variant="button"
                              sx={{ fontSize: 12, mb: 0.5, display: "block" }}
                            >
                              Quantidade
                            </MDTypography>
                            <TextField
                              value={l.qtd ?? ""}
                              onChange={(e) =>
                                setCampo(r, "qtd", onlyNumberOrEmpty(e.target.value))
                              }
                              onKeyDown={handleEnterFixar}
                              inputProps={{ step: "0.01", min: "0" }}
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: 2,
                                  fontSize: 13,
                                  width: 90,
                                },
                              }}
                            />
                          </MDBox>
                        )}

                        {usaEuros && (
                          <MDBox sx={{ minWidth: 90 }}>
                            <MDTypography
                              variant="button"
                              sx={{ fontSize: 12, mb: 0.5, display: "block" }}
                            >
                              Euros
                            </MDTypography>
                            <TextField
                              value={l.euros ?? ""}
                              onChange={(e) =>
                                setCampo(r, "euros", onlyNumberOrEmpty(e.target.value))
                              }
                              onKeyDown={handleEnterFixar}
                              inputProps={{ step: "0.01", min: "0" }}
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: 2,
                                  fontSize: 13,
                                  width: 90,
                                },
                              }}
                            />
                          </MDBox>
                        )}

                        {usaLitros && (
                          <MDBox sx={{ minWidth: 90 }}>
                            <MDTypography
                              variant="button"
                              sx={{ fontSize: 12, mb: 0.5, display: "block" }}
                            >
                              Litros
                            </MDTypography>
                            <TextField
                              value={l.litros ?? ""}
                              onChange={(e) =>
                                setCampo(r, "litros", onlyNumberOrEmpty(e.target.value))
                              }
                              onKeyDown={handleEnterFixar}
                              inputProps={{ step: "0.01", min: "0" }}
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: 2,
                                  fontSize: 13,
                                  width: 90,
                                },
                              }}
                            />
                          </MDBox>
                        )}

                        {usaClientes && (
                          <MDBox sx={{ minWidth: 90 }}>
                            <MDTypography
                              variant="button"
                              sx={{ fontSize: 12, mb: 0.5, display: "block" }}
                            >
                              Clientes
                            </MDTypography>
                            <TextField
                              value={l.clientes ?? ""}
                              onChange={(e) =>
                                setCampo(r, "clientes", onlyNumberOrEmpty(e.target.value))
                              }
                              onKeyDown={handleEnterFixar}
                              inputProps={{ step: "0.01", min: "0" }}
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: 2,
                                  fontSize: 13,
                                  width: 90,
                                },
                              }}
                            />
                          </MDBox>
                        )}
                      </MDBox>
                    </MDBox>
                  );
                })}
              </MDBox>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <MDButton color="info" onClick={onClose}>
          Fechar
        </MDButton>
        <MDButton color="success" onClick={handleApply} disabled={loading || !podeAplicar}>
          Aplicar
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}
